// ── Image Uploader Component ─────────────────────────────────────────────────
// Supports: placeholder, URL import, local file upload, screen capture with crop

import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, Upload, Link, Trash2, Loader2, Crop } from 'lucide-react';
import { getToken } from '../../lib/auth';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB (matches backend limit)
const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

async function uploadFileToBackend(file) {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const formData = new FormData();
  formData.append('file', file);
  const resp = await fetch(`${API_BASE}/api/files/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(err.detail || 'Upload failed');
  }
  const data = await resp.json();
  return data.url.startsWith('/') ? `${API_BASE}${data.url}` : data.url;
}

// ── Cropper Overlay (full-screen, two-click selection) ────────────────────────

function CropperOverlay({ imageDataUrl, onConfirm, onCancel, uploading }) {
  const [start, setStart] = useState(null);   // { x, y } — first click
  const [end, setEnd] = useState(null);       // { x, y } — second click
  const overlayRef = useRef(null);

  const getRelativePos = useCallback((e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const handleClick = (e) => {
    if (e.button !== 0) return;
    const pos = getRelativePos(e);

    if (!start) {
      // First click — set start point
      setStart(pos);
      setEnd(null);
    } else if (!end) {
      // Second click — set end point, finalize selection
      setEnd(pos);
    } else {
      // Reset if clicking again after selection is done
      setStart(pos);
      setEnd(null);
    }
  };

  // Calculate selection rect
  const makeRect = useCallback(() => {
    if (!start || !end) return null;
    return {
      left:   Math.min(start.x, end.x),
      top:    Math.min(start.y, end.y),
      width:  Math.abs(end.x - start.x),
      height: Math.abs(end.y - start.y),
    };
  }, [start, end]);

  const rect = makeRect();
  const w = rect?.width ?? 0;
  const h = rect?.height ?? 0;
  const hasSelection = w > 20 && h > 20;

  const handleConfirm = () => {
    if (!rect || !hasSelection) return;
    onConfirm({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
  };

  // Instruction text
  const instruction = !start
    ? 'Click to set the starting point of your selection'
    : !end
      ? 'Click to set the end point — the area will be captured'
      : hasSelection
        ? `${Math.round(w)} × ${Math.round(h)} px — Click Confirm to upload, or click again to reselect`
        : 'Selection too small — click again to reselect';

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{ background: 'rgba(0,0,0,0.85)' }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-black/60 text-white">
        <span className="text-sm font-medium">{instruction}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={handleConfirm}
            disabled={!hasSelection || uploading}
            className="flex items-center gap-1 px-4 py-1.5 text-sm font-semibold rounded-md bg-[#c9a84c] text-black hover:bg-[#e0c060] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {uploading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
              : <><Crop className="w-4 h-4" /> Confirm</>
            }
          </button>
          <button
            onClick={onCancel}
            disabled={uploading}
            className="px-4 py-1.5 text-sm font-semibold rounded-md bg-white/15 text-white hover:bg-white/25 transition-all disabled:opacity-40"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div
          ref={overlayRef}
          className="relative max-w-full max-h-full select-none"
          style={{ cursor: 'crosshair' }}
          onClick={handleClick}
        >
          <img
            src={imageDataUrl}
            alt="Screenshot to crop"
            className="max-w-full max-h-[calc(100vh-90px)] object-contain"
            draggable={false}
          />

          {/* Start point marker (after first click, before second) */}
          {start && !end && (
            <div className="absolute" style={{ left: start.x - 1, top: start.y - 1 }}>
              {/* Crosshair at start point */}
              <div className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2">
                <div className="absolute top-1/2 left-0 w-full h-0.5 bg-[#c9a84c]" />
                <div className="absolute left-1/2 top-0 w-0.5 h-full bg-[#c9a84c]" />
              </div>
            </div>
          )}

          {/* Full selection rect (after second click) */}
          {rect && (
            <>
              {/* Selection box with golden dashed border */}
              <div
                className="absolute pointer-events-none"
                style={{
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height,
                  outline: '2px dashed #c9a84c',
                  outlineOffset: '-1px',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* Status bar */}
      <div className="text-center py-1.5 text-xs bg-black/40">
        {!start && <span className="text-white/50">Click once to mark the starting point</span>}
        {start && !end && (
          <span className="text-[#c9a84c]">
            Start: ({Math.round(start.x)}, {Math.round(start.y)}) — Click again to set end point
          </span>
        )}
        {rect && (
          <span className={hasSelection ? 'text-green-400' : 'text-red-400'}>
            {hasSelection ? '✓ ' : '✗ '}
            Selection: {Math.round(w)} × {Math.round(h)} px
            {!hasSelection && ' (minimum 20×20 px)'}
          </span>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ImageUploader({
  value = '',
  sourceType = 'placeholder',
  onChange,
  onRemove,
  label = 'Image',
  aspectRatio = '4:3',
  height = '160px',
  compact = false,
}) {
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const fileInputCompactRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [capturing, setCapturing] = useState(false);

  // Cropper state
  const [snippingImage, setSnippingImage] = useState(null);   // data URL of full screenshot
  const [cropperUploading, setCropperUploading] = useState(false);

  const computedHeight = compact ? '80px' : height;
  const hasImage = value && sourceType !== 'placeholder';

  // ── Screen capture → open cropper ───────────────────────────────────────
  const handleCapture = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setError('Screen capture is not supported in this browser. Please use Upload instead.');
      return;
    }

    setError('');
    setCapturing(true);
    let stream = null;
    let video = null;

    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor' },
        audio: false,
      });

      video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;

      await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => video.play().then(resolve).catch(reject);
        video.onerror = reject;
      });

      await new Promise(r => setTimeout(r, 300));

      // Capture frame to canvas → data URL
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // Clean up stream immediately
      stream.getTracks().forEach(t => t.stop());
      video.remove();
      stream = null;
      video = null;

      const dataUrl = canvas.toDataURL('image/png');
      setSnippingImage(dataUrl);

    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Screen capture failed');
      }
    } finally {
      if (stream) stream.getTracks().forEach(t => t.stop());
      if (video) video.remove();
      setCapturing(false);
    }
  };

  // ── Cropper confirm — crop & upload ─────────────────────────────────────
  const handleCropConfirm = async ({ x, y, w, h }) => {
    if (!snippingImage) return;

    setCropperUploading(true);
    try {
      // Load the full image into an Image element to get accurate dimensions
      const img = new window.Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = snippingImage;
      });

      // Map cropper coordinates (which are CSS-scaled) to actual image pixels
      const containerEl = document.querySelector('[class*="max-w-full max-h-full"]');
      const scaleX = img.naturalWidth / (containerEl?.clientWidth || img.naturalWidth);
      const scaleY = img.naturalHeight / (containerEl?.clientHeight || img.naturalHeight);

      const sx = Math.round(x * scaleX);
      const sy = Math.round(y * scaleY);
      const sw = Math.round(w * scaleX);
      const sh = Math.round(h * scaleY);

      // Crop via canvas
      const canvas = document.createElement('canvas');
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Crop failed')), 'image/png');
      });

      if (blob.size > MAX_FILE_SIZE) {
        setError(`Cropped image too large (${(blob.size/(1024*1024)).toFixed(1)}MB). Try a smaller area.`);
        setSnippingImage(null);
        setCropperUploading(false);
        return;
      }

      const file = new File([blob], `snip_${Date.now()}.png`, { type: 'image/png' });
      const url = await uploadFileToBackend(file);
      onChange?.({ url, sourceType: 'local' });
    } catch (err) {
      setError(err.message || 'Failed to crop and upload');
    } finally {
      setSnippingImage(null);
      setCropperUploading(false);
    }
  };

  const handleCropCancel = () => {
    setSnippingImage(null);
  };

  // ── Local file upload (via backend API) ─────────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError(`Image must be under ${MAX_FILE_SIZE / (1024*1024)}MB.`);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    setError('');
    setUploading(true);
    try {
      const url = await uploadFileToBackend(file);
      onChange?.({ url, sourceType: 'local' });
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
    e.target.value = '';
  };

  // ── URL import ─────────────────────────────────────────────────────────
  const handleUrlImport = () => {
    if (!urlInput.trim()) return;
    setError('');
    onChange?.({ url: urlInput.trim(), sourceType: 'url' });
    setUrlInput('');
    setShowUrlInput(false);
  };

  // ── Remove ──────────────────────────────────────────────────────────────
  const handleRemove = () => {
    setError('');
    setUrlInput('');
    onRemove?.();
  };

  return (
    <div className={compact ? '' : 'mb-3'}>
      {label && !compact && (
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
          {label}
        </label>
      )}

      {/* Image preview / placeholder */}
      <div
        className="relative rounded-lg overflow-hidden border border-[#e5e4df] bg-[#f3f2ef]"
        style={{ height: computedHeight }}
      >
        {hasImage ? (
          <>
            <img
              src={value}
              alt={label}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 flex-col items-center justify-center text-[#9ca3af]" style={{ display: 'none' }}>
              <ImageIcon className={compact ? 'w-6 h-6 mb-1' : 'w-10 h-10 mb-2'} />
              <span className="text-xs">Image failed to load</span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#9ca3af]">
            <ImageIcon className={compact ? 'w-6 h-6 mb-1' : 'w-10 h-10 mb-2'} />
            {!compact && (
              <span className="text-xs">Upload, Snip, or paste a URL</span>
            )}
          </div>
        )}
      </div>

      {/* ── Non-compact controls ───────────────────────────────────────────── */}
      {!compact && (
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || capturing}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-[#e5e4df] hover:bg-[#f8f7f4] text-[#6b7280] hover:text-[#1a1a2e] transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

          <button
            type="button"
            onClick={handleCapture}
            disabled={uploading || capturing}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-[#e5e4df] hover:bg-[#f8f7f4] text-[#6b7280] hover:text-[#1a1a2e] transition-colors disabled:opacity-50"
            title="Capture screen then select area"
          >
            {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Crop className="w-3.5 h-3.5" />}
            {capturing ? 'Capturing...' : 'Snip'}
          </button>

          <button
            type="button"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-[#e5e4df] hover:bg-[#f8f7f4] text-[#6b7280] hover:text-[#1a1a2e] transition-colors"
          >
            <Link className="w-3.5 h-3.5" />
            URL
          </button>

          {hasImage && (
            <button
              type="button"
              onClick={handleRemove}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove
            </button>
          )}
        </div>
      )}

      {/* ── Compact controls ────────────────────────────────────────────────── */}
      {compact && (
        <div className="flex items-center gap-1 mt-1">
          <button
            type="button"
            onClick={() => fileInputCompactRef.current?.click()}
            disabled={uploading || capturing}
            className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] transition-colors disabled:opacity-50"
            title="Upload image"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          </button>
          <input ref={fileInputCompactRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

          <button
            type="button"
            onClick={handleCapture}
            disabled={uploading || capturing}
            className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] transition-colors disabled:opacity-50"
            title="Capture screenshot"
          >
            {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Crop className="w-3.5 h-3.5" />}
          </button>

          <button
            type="button"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] transition-colors"
            title="Import from URL"
          >
            <Link className="w-3.5 h-3.5" />
          </button>
          {hasImage && (
            <button
              type="button"
              onClick={handleRemove}
              className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
              title="Remove image"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* URL input */}
      {showUrlInput && !compact && (
        <div className="flex items-center gap-2 mt-2">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUrlImport()}
            placeholder="https://example.com/image.jpg"
            className="flex-1 px-3 py-1.5 text-xs border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
          />
          <button
            type="button"
            onClick={handleUrlImport}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-[#1a1a2e] text-white hover:bg-[#252545] transition-colors"
          >
            Apply
          </button>
        </div>
      )}
      {showUrlInput && compact && (
        <div className="flex items-center gap-1 mt-1">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUrlImport()}
            placeholder="https://..."
            className="flex-1 px-2 py-1 text-xs border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
          />
          <button type="button" onClick={handleUrlImport} className="px-2 py-1 text-xs rounded bg-[#1a1a2e] text-white">
            OK
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}

      {/* ── Cropper Overlay (portal to body) ────────────────────────────────── */}
      {snippingImage && (
        <CropperOverlay
          imageDataUrl={snippingImage}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          uploading={cropperUploading}
        />
      )}
    </div>
  );
}
